// Campaign scenario dressing: deterministic post-createGame mutations that turn a procedural level
// into a STAGED one (castles with walls and gates, garrisons, forced AI temperaments). Mirrors the
// tutorial-chapter precedent (mutate the GameState in the UI-adjacent layer, keep src/game pure).
// Imported ONLY by main.ts; storage/achievements must never depend on this module.

import type { FactionId, GameState, Region, UnitType } from '../game/types';
import { NEUTRAL, impassable } from '../game/types';
import { createGame, createMatch, unitMaxHp, unitMoves } from '../game/engine';
import { CAMPAIGN } from './campaign';

const unit = (type: UnitType, owner: FactionId, hp?: number): NonNullable<Region['unit']> => ({
  type,
  owner,
  movesLeft: unitMoves(type),
  hp: hp ?? unitMaxHp(type),
});

/** Wall off a whole DISTRICT of the map: the castle lord's home province becomes a fortified region.
 *  The province's landward border tiles turn into a running rampart (the sea seals the rest), the
 *  road pierces it at garrisoned GATES, every interior tile belongs to the lord from the start, and
 *  the lord's AI is flagged `garrison`: his soldiers hold the walls and never march out. Storming a
 *  gate (or arrowing over the wall) is the ONLY way in, and the keep inside is the prize.
 *
 *  Gate picking: road tiles crossing the wall line first (the king's road runs through the gate,
 *  which is exactly how a pass fort reads), then any line tile facing ground the player can reach. */
function buildEnclosure(g: GameState, lord: FactionId, gateCount: number, garrison: UnitType[]): void {
  const cap = g.regions.find((r) => r.isCapital && r.owner === lord);
  if (!cap) return;
  const province = cap.island;
  const inside = (r: Region): boolean => r.island === province && !impassable(r.terrain);
  // the wall line: province tiles that touch walkable land OUTSIDE the province (coast edges stay
  // open water; mountains already seal). Capital never walls (it is the keep).
  const line = g.regions.filter((r) => inside(r) && !r.isCapital && r.neighbors.some((n) => g.regions[n]!.island !== province && !impassable(g.regions[n]!.terrain)));
  if (line.length < 2) return; // nothing meaningful to wall on this seed
  const lineIds = new Set(line.map((r) => r.id));
  const roadCross = line.filter((r) => r.terrain === 'road').map((r) => r.id); // the king's road pierces the wall HERE
  const outsideOf = new Map<number, number[]>();
  for (const reg of line) outsideOf.set(reg.id, reg.neighbors.filter((n) => g.regions[n]!.island !== province));
  // 1. raise the rampart
  for (const reg of line) {
    reg.terrain = 'wall';
    reg.owner = NEUTRAL;
    reg.unit = null;
    reg.production = null;
    reg.buildings = [];
    for (const nId of reg.neighbors) {
      const n = g.regions[nId]!;
      n.neighbors = n.neighbors.filter((x) => x !== reg.id);
    }
    reg.neighbors = [];
  }
  // 2. what can the besieger still reach?
  const start = g.regions.find((r) => r.isCapital && r.owner === g.playerFaction);
  const reachable = new Set<number>();
  if (start) {
    const stack = [start.id];
    while (stack.length) {
      const id = stack.pop()!;
      if (reachable.has(id)) continue;
      reachable.add(id);
      for (const n of g.regions[id]!.neighbors) stack.push(n);
    }
  }
  // 3. open the gates: road crossings first, then any line tile whose outside face is reachable
  const facesWorld = (id: number): boolean => (outsideOf.get(id) ?? []).some((n) => reachable.has(n));
  const roadGates = roadCross.filter((id) => lineIds.has(id) && facesWorld(id));
  const otherGates = line.map((r) => r.id).filter((id) => !roadGates.includes(id) && facesWorld(id));
  const picks = [...roadGates, ...otherGates];
  const usable = picks.length ? picks : line.map((r) => r.id); // degrade rather than seal the district forever
  const gates = Math.max(1, Math.min(gateCount, usable.length));
  const step = usable.length / gates;
  for (let gi = 0; gi < gates; gi++) {
    const reg = g.regions[usable[Math.min(usable.length - 1, Math.floor(gi * step))]!]!;
    if (reg.terrain === 'gate') continue;
    reg.terrain = 'gate';
    reg.owner = lord;
    reg.unit = unit(garrison[gi % garrison.length] ?? 3, lord);
    // reconnect the gate both ways: to the world outside and to the district inside
    reg.neighbors = [];
    for (const n of g.regions) {
      if (impassable(n.terrain) || n.id === reg.id) continue;
      if (dist(n, reg) === 1) {
        reg.neighbors.push(n.id);
        if (!n.neighbors.includes(reg.id)) n.neighbors.push(reg.id);
      }
    }
  }
  // 4. the district is the lord's from the start: every interior tile flies his banner (the walls are
  // his fortress-city, not empty countryside), and his AI holds them (garrison: never marches out)
  for (const reg of g.regions) {
    if (reg.island === province && !impassable(reg.terrain) && reg.owner === NEUTRAL) reg.owner = lord;
  }
  const f = g.factions[lord];
  if (f) f.garrison = true;
}

/** Force an AI faction into a temperament the scenario's story needs (e.g. castle lords hold their keep). */
function temper(g: GameState, fid: FactionId, strategy: GameState['factions'][number]['strategy']): void {
  const f = g.factions[fid];
  if (f && !f.isPlayer) f.strategy = strategy;
}

/** The player's most dramatic rival: the AI faction whose capital sits farthest from the player's. */
function farthestRival(g: GameState): FactionId {
  const mine = g.regions.find((r) => r.isCapital && r.owner === g.playerFaction);
  let best: FactionId = -1;
  let bestD = -1;
  for (const reg of g.regions) {
    if (!reg.isCapital || reg.owner === g.playerFaction || reg.owner < 0) continue;
    const d = mine ? Math.hypot(reg.q - mine.q, reg.r - mine.r) : 0;
    if (d > bestD) {
      bestD = d;
      best = reg.owner;
    }
  }
  return best;
}

/** Every AI rival, nearest-first (for staging lesser castles on the closer lords). */
function rivals(g: GameState): FactionId[] {
  const mine = g.regions.find((r) => r.isCapital && r.owner === g.playerFaction);
  return g.regions
    .filter((r) => r.isCapital && r.owner !== g.playerFaction && r.owner >= 0)
    .sort((a, b) => (mine ? Math.hypot(a.q - mine.q, a.r - mine.r) - Math.hypot(b.q - mine.q, b.r - mine.r) : 0))
    .map((r) => r.owner);
}

/** Declare an already-raging, aged war (strikes allowed from turn one): ambush levels start hot. */
function openWar(g: GameState, a: FactionId, b: FactionId): void {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (!g.wars.some(([x, y]) => x === lo && y === hi)) g.wars.push([lo, hi]);
  g.warStamp[`${lo}-${hi}`] = -9;
}

/** Turn a tile into impassable mountain scenery (stripped from the walkable graph both ways). */
function mountainize(g: GameState, reg: Region): void {
  reg.terrain = 'mountain';
  reg.owner = NEUTRAL;
  reg.unit = null;
  reg.production = null;
  reg.buildings = [];
  for (const nId of reg.neighbors) {
    const n = g.regions[nId]!;
    n.neighbors = n.neighbors.filter((x) => x !== reg.id);
  }
  reg.neighbors = [];
}

const dist = (a: Region, b: Region): number => (Math.abs(a.q - b.q) + Math.abs(a.q + a.r - b.q - b.r) + Math.abs(a.r - b.r)) / 2;

// One dressing function per staged level, keyed by CampaignLevel.id. Levels without an entry play
// their procedural setup as-is. All mutations are deterministic (no RNG), so a level is the same
// battlefield for everyone and across resumes (the save persists the mutated board anyway).
const SCENARIOS: Record<string, (g: GameState) => void> = {
  // L1 - The King's Road: an actual dawn ambush. Raiders of the nearest rival already stand ON the
  // road two-three tiles out, the war is hot from turn one, and a caravan guard joins the player's
  // capital garrison, so the level opens 2v2 on the road it is named after.
  l01: (g) => {
    const raiderFac = rivals(g)[0];
    if (raiderFac == null) return;
    const myCap = g.regions.find((r) => r.isCapital && r.owner === g.playerFaction);
    if (!myCap) return;
    openWar(g, g.playerFaction, raiderFac);
    temper(g, raiderFac, 'aggressive');
    // the caravan guard: a spearman on the road beside the capital (spears turn away riders)
    const guardSpot = myCap.neighbors.map((n) => g.regions[n]!).filter((r) => r.terrain === 'road' && !r.unit).sort((a, b) => a.id - b.id)[0];
    if (guardSpot) {
      guardSpot.owner = g.playerFaction;
      guardSpot.unit = unit(3, g.playerFaction);
    }
    // the ambushers: two bloodied raider warriors on the road, close enough to threaten this turn
    const roadOut = g.regions
      .filter((r) => r.terrain === 'road' && !r.unit && r.owner === NEUTRAL && dist(r, myCap) >= 2 && dist(r, myCap) <= 4)
      .sort((a, b) => dist(a, myCap) - dist(b, myCap) || a.id - b.id);
    for (const spot of roadOut.slice(0, 2)) {
      spot.owner = raiderFac;
      spot.unit = unit(0, raiderFac, 3);
    }
  },
  // L2 - Ashen Fields: the burned farms are real ruins. A band of neutral tiles near the player
  // holds pre-built Markets (reclaimable economy: capture them and the income is yours), telling
  // the rebuild story on the board instead of only in the banner.
  l02: (g) => {
    const myCap = g.regions.find((r) => r.isCapital && r.owner === g.playerFaction);
    if (!myCap) return;
    const ruins = g.regions
      .filter((r) => !r.terrain && !r.unit && !r.isCapital && r.owner === NEUTRAL && dist(r, myCap) >= 2 && dist(r, myCap) <= 4)
      .sort((a, b) => dist(a, myCap) - dist(b, myCap) || a.id - b.id)
      .slice(0, 3);
    for (const r of ruins) r.buildings = [0]; // BUILDING.Market: a ruin worth reclaiming
  },
  // L3 - The Mountain Pass: the nearest rival's WHOLE province is a walled district with a single
  // gate where the king's road pierces the rampart, and the approach road threads a real pass:
  // the ground flanking the last stretch grows mountain ridges. One gate, one way through.
  // S1 - First Steel: the combat school. The warband is already ON the road: a worn rider and a
  // warrior hold two forward tiles, the player opens with a spearman escort, war from turn one (the
  // level flag). The guide then walks the two teaching duels: spear turns horse, steel turns fools.
  s01: (g) => {
    const foe = rivals(g)[0];
    if (foe == null) return;
    temper(g, foe, 'defensive'); // the warband holds its road; the PLAYER walks into the duels
    const myCap = g.regions.find((r) => r.isCapital && r.owner === g.playerFaction);
    const foeCap = g.regions.find((r) => r.isCapital && r.owner === foe);
    if (!myCap || !foeCap) return;
    // the escort: a spearman beside the capital (the anti-cavalry lesson needs a spear in hand)
    const escort = myCap.neighbors.map((n) => g.regions[n]!).filter((r) => !r.unit && !r.isCapital && r.terrain !== 'mountain').sort((a, b) => a.id - b.id)[0];
    if (escort) {
      escort.owner = g.playerFaction;
      escort.unit = unit(3, g.playerFaction);
    }
    // the warband's forward line is a CHAIN from the escort toward their camp (escort -> rider ->
    // brawler, each adjacent to the last): the guide walks duel one as a single strike, and after the
    // spear advances onto the fallen rider's tile, duel two is a single strike the following turn
    const open = (r: { owner: number; unit: unknown; terrain?: string }): boolean => r.owner === NEUTRAL && !r.unit && r.terrain !== 'mountain';
    const towardFoe = (from: { neighbors: number[] }, but: Set<number>): (typeof g.regions)[number] | undefined =>
      from.neighbors
        .map((n) => g.regions[n]!)
        .filter((r) => open(r) && !but.has(r.id) && dist(r, foeCap) < dist(from as never, foeCap))
        .sort((a, b) => dist(a, foeCap) - dist(b, foeCap) || a.id - b.id)[0];
    if (escort) {
      const used = new Set([myCap.id, escort.id]);
      const riderSpot = towardFoe(escort, used);
      if (riderSpot) {
        used.add(riderSpot.id);
        riderSpot.owner = foe;
        riderSpot.unit = unit(2, foe, 2); // rider at 2 HP: one clean spear thrust ends it
        const brawlSpot = towardFoe(riderSpot, used);
        if (brawlSpot) {
          brawlSpot.owner = foe;
          brawlSpot.unit = unit(0, foe, 2); // a bloodied warrior one step on: the next turn's single strike
        }
      }
    }
  },
  l03: (g) => {
    const lord = rivals(g)[0];
    if (lord == null) return;
    const cap = g.regions.find((r) => r.isCapital && r.owner === lord);
    if (!cap) return;
    buildEnclosure(g, lord, 1, [3]); // one gate on the road, a spearman holds it
    temper(g, lord, 'defensive');
    // ridge up the flanks of the approach road so the walled gate sits in a true mountain pass
    const approach = g.regions.filter((r) => r.terrain === 'road' && dist(r, cap) <= 6);
    for (const road of approach) {
      for (const nId of [...road.neighbors]) {
        const n = g.regions[nId]!;
        if (n.terrain || n.unit || n.isCapital || n.owner !== NEUTRAL) continue;
        if (dist(n, cap) > 6 || dist(n, cap) < 3) continue; // keep the gate's own doorstep open
        if (n.neighbors.some((id) => g.regions[id]!.terrain === 'gate')) continue; // never brick up the approach
        mountainize(g, n);
      }
    }
  },
  // L4 - Gates of Greyhold: the far lord's province is Greyhold itself, a walled city with TWO
  // garrisoned gates (spear on the road gate, bow on the flank). His host never leaves its walls.
  l04: (g) => {
    const lord = farthestRival(g);
    if (lord < 0) return;
    buildEnclosure(g, lord, 2, [3, 1]);
    temper(g, lord, 'defensive');
  },
  // L5 - The Last Keep: TWO walled districts. The near lord holds a one-gate fort, the far lord the
  // great city with two gates and a rider mustered inside as a mobile reserve.
  l05: (g) => {
    g.factions[g.playerFaction]!.techs.push('mil1'); // Archery marched with you: the lesson here is Cavalry (mil2)
    const rs = rivals(g);
    const near = rs[0];
    const far = rs[rs.length - 1];
    if (near != null && near !== far) {
      buildEnclosure(g, near, 1, [3]);
      temper(g, near, 'defensive');
    }
    if (far != null) {
      buildEnclosure(g, far, 2, [3, 1]);
      const cap2 = g.regions.find((r) => r.isCapital && r.owner === far);
      // the mobile reserve: a rider on an interior tile beside the keep (garrison rules: it defends
      // the district, repositioning on the lord's own ground, but never rides out)
      const yard = cap2?.neighbors.map((id) => g.regions[id]!).find((r) => !r.unit && !r.terrain && r.owner === far);
      if (yard) yard.unit = unit(2, far);
      temper(g, far, 'defensive');
    }
  },
};

/** Castle walls can orphan a tile whose only paths ran through the ring (a nook pinched between the
 *  wall and the coast). Seal such pockets into wall scenery too: they read as the castle meeting the
 *  cliffs, and no unclaimable-but-walkable dead tile is left on the board. The keep itself is safe:
 *  it always keeps its gate(s). */
function sealOrphans(g: GameState): void {
  const start = g.regions.find((r) => r.isCapital && r.owner === g.playerFaction);
  if (!start) return;
  const seen = new Set<number>();
  const stack = [start.id];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const n of g.regions[id]!.neighbors) stack.push(n);
  }
  for (const reg of g.regions) {
    if (impassable(reg.terrain) || seen.has(reg.id)) continue;
    reg.terrain = 'wall';
    reg.owner = NEUTRAL;
    reg.unit = null;
    reg.production = null;
    reg.buildings = [];
    for (const nId of reg.neighbors) {
      const n = g.regions[nId]!;
      n.neighbors = n.neighbors.filter((x) => x !== reg.id);
    }
    reg.neighbors = [];
  }
}

/** Apply a level's staged dressing (no-op for levels that play their procedural setup). */
export function applyScenario(g: GameState, levelId: string): void {
  const fn = SCENARIOS[levelId];
  if (!fn) return;
  fn(g);
  sealOrphans(g);
}

/** THE campaign level builder: the fixed match, its staged dressing, and the level's own victory
 *  condition (a share of the map, or the one place whose capture ends it). Every caller uses this,
 *  so what the tests measure is exactly what the game ships. */
export function buildCampaignLevel(index: number, seedOverride?: number): GameState {
  const lv = seedOverride === undefined ? CAMPAIGN[index]! : { ...CAMPAIGN[index]!, seed: seedOverride };
  const dom = lv.goal.kind === 'domination' ? lv.goal.pct : 1;
  // a level may cast its rivals: two bands of the SAME people (raiders), named for what they are
  const g = lv.rivalRaces
    ? createMatch({
        seed: lv.seed,
        factionCount: lv.factions,
        islandSize: lv.islandSize,
        dominationPct: dom,
        difficulty: lv.difficulty,
        mapKind: lv.mapKind,
        mode: 'solo',
        slots: [{ race: lv.race, human: true }, ...lv.rivalRaces.map((race) => ({ race }))],
      })
    : createGame(lv.seed, lv.race, lv.factions, lv.islandSize, dom, lv.difficulty, lv.mapKind, false, lv.heart ?? false);
  if (lv.rivalNames) {
    // rename and re-tint the bands so two orc warbands never read as one faction on the map
    const RAIDER_TINTS = [0xd94f2a, 0x8f3f1c, 0xb2622e];
    g.factions.filter((f) => !f.isPlayer).forEach((f, i) => {
      f.name = lv.rivalNames![i] ?? f.name;
      if (lv.rivalRaces && lv.rivalRaces.length > 1) f.color = RAIDER_TINTS[i % RAIDER_TINTS.length]!;
    });
  }
  // campaign difficulty dial: every rival carries the level's handicap (never set in free play or MP)
  if (lv.handicap) for (const f of g.factions) if (!f.isPlayer) f.handicap = { ...lv.handicap };
  if (lv.rivalsFight) {
    // the bands are rivals of each other as well, so their armies grind on something besides your border
    const rivals = g.factions.filter((f) => !f.isPlayer).map((f) => f.id);
    for (let i = 0; i < rivals.length; i++) for (let j = i + 1; j < rivals.length; j++) openWar(g, rivals[i]!, rivals[j]!);
  }
  if (lv.noParley) {
    // no diplomacy here: every rival refuses to talk, and the war is already on
    for (const f of g.factions) {
      if (f.isPlayer) continue;
      f.noParley = true;
      openWar(g, g.playerFaction, f.id);
    }
  }
  if (lv.muteParley) {
    // Conquest Trail 2.0: diplomacy is not TAUGHT yet, so only the PLAYER cannot parley (no offers in,
    // none out); rivals still talk among themselves and wars keep their natural clock
    g.factions[g.playerFaction]!.noParley = true;
  }
  applyScenario(g, lv.id);
  if (lv.goal.kind === 'capture') {
    // pick the prize deterministically from the STAGED board: a walled lord's keep, or a rival seat
    const mine = g.regions.find((r) => r.isCapital && r.owner === g.playerFaction);
    const caps = g.regions.filter((r) => r.isCapital && r.owner >= 0 && r.owner !== g.playerFaction);
    const byDist = [...caps].sort((a, b) => (mine ? dist(a, mine) - dist(b, mine) : a.id - b.id));
    const keep = caps.find((r) => g.factions[r.owner]?.garrison);
    const target = lv.goal.target === 'keep' ? (keep ?? byDist[byDist.length - 1]) : lv.goal.target === 'farCapital' ? byDist[byDist.length - 1] : byDist[0];
    if (target) g.objective = { kind: 'capture', region: target.id, name: lv.goal.name };
  }
  return g;
}
